import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DownloadTaskStatus, DownloadTaskType } from 'constants/enums'
import { thunks as DownloadThunks } from 'reducers/Downloads'
import { PlayListService } from 'services'
import { trackDownloadEvent } from 'utils/analytics'
import { browserAllowsDownload } from 'utils/browserHelpers'
import downloadPlaylistPdf from 'utils/downloadPlaylistPdf'

const EMPTY_DOWNLOADS = []
const FORMATS = ['wav', 'mp3', 'pdf']
const selectDownloads = ({ downloads }) => downloads?.downloads || EMPTY_DOWNLOADS
const requestKey = (playlist, format) =>
  JSON.stringify([String(playlist.playlistid), format])

const hasPlaylistId = (playlist) =>
  playlist?.playlistid !== undefined && playlist?.playlistid !== null

const isKnownEmpty = (playlist) => {
  if (Array.isArray(playlist.songs)) return playlist.songs.length === 0
  if (playlist.tracks === null || playlist.tracks === undefined) return false

  const count = Number(playlist.tracks)
  return Number.isFinite(count) && count <= 0
}

const isActiveDownload = (download, playlist, format) =>
  download.type === DownloadTaskType.playlist &&
  String(download.mediaID) === String(playlist.playlistid) &&
  download.format === format &&
  [DownloadTaskStatus.waiting, DownloadTaskStatus.loading].includes(
    download.status
  )

const downloadDescriptor = (playlist, format, source) => ({
  artist: '',
  format,
  mediaID: playlist.playlistid,
  sourceLabel: source,
  title: playlist.name,
  trackcount: playlist.tracks,
  type: DownloadTaskType.playlist
})

const trackDownload = (event, download) => {
  try {
    trackDownloadEvent(event, download)
  } catch (error) {
    console.warn(error)
  }
}

const usePlaylistDownloads = () => {
  const dispatch = useDispatch()
  const downloads = useSelector(selectDownloads)
  const mountedRef = useRef(true)
  const pendingRef = useRef(new Set())
  const tracklistsRef = useRef(new Map())
  const [pendingKeys, setPendingKeys] = useState([])
  const [downloadFailure, setDownloadFailure] = useState(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const getDownloadAvailability = useCallback((playlist) => {
    const hasSongs = hasPlaylistId(playlist) && !isKnownEmpty(playlist)
    const hasAudioPermission = !Array.isArray(playlist?.songs) ||
      playlist.songs.some((song) => song.candownload === true)
    const available = (format) =>
      browserAllowsDownload && hasSongs &&
      !pendingKeys.includes(requestKey(playlist, format)) &&
      (format === 'pdf' ||
        (hasAudioPermission && !downloads.some((download) =>
          isActiveDownload(download, playlist, format)
        )))

    return { wav: available('wav'), mp3: available('mp3'), pdf: available('pdf') }
  }, [downloads, pendingKeys])

  const loadSongs = useCallback((playlistId) => {
    const key = String(playlistId)
    let request = tracklistsRef.current.get(key)

    if (!request) {
      request = Promise.resolve()
        .then(() => PlayListService.getTracklist(playlistId))
        .then((response) => {
          if (!Array.isArray(response?.data?.items)) {
            throw new Error('Invalid playlist tracklist response')
          }
          return response.data.items
        })
        .finally(() => tracklistsRef.current.delete(key))
      tracklistsRef.current.set(key, request)
    }
    return request
  }, [])

  const requestPlaylistDownload = useCallback(async (
    playlist,
    format = 'mp3',
    source = 'unknown'
  ) => {
    if (!hasPlaylistId(playlist) || !FORMATS.includes(format)) return false
    const request = { playlist, format, source }
    const fail = (messageKey, retryable = false) => {
      if (mountedRef.current) {
        setDownloadFailure({ ...request, messageKey, retryable })
      }
      return false
    }

    if (!browserAllowsDownload) {
      return fail('playlist.browser-download-unavailable')
    }
    if (isKnownEmpty(playlist)) return fail('playlist.empty-download-message')

    const key = requestKey(playlist, format)
    if (pendingRef.current.has(key) || (format !== 'pdf' &&
      downloads.some((download) => isActiveDownload(download, playlist, format))
    )) return false

    pendingRef.current.add(key)
    if (mountedRef.current) {
      setPendingKeys([...pendingRef.current])
      setDownloadFailure(null)
    }

    let descriptor = downloadDescriptor(playlist, format, source)
    try {
      const songs = await loadSongs(playlist.playlistid)
      if (songs.length === 0) return fail('playlist.empty-download-message')

      const hydrated = { ...playlist, songs, tracks: songs.length }
      descriptor = downloadDescriptor(hydrated, format, source)
      if (format === 'pdf') {
        trackDownload('Download Start', descriptor)
        await downloadPlaylistPdf(hydrated)
        trackDownload('Download Complete', descriptor)
      } else {
        const downloadable = songs.filter((song) => song.candownload === true)
        if (downloadable.length === 0) {
          return fail('playlist.no-downloadable-tracks')
        }

        const queued = await dispatch(DownloadThunks.addPlaylistToDownloadQueue(
          { ...hydrated, songs: downloadable, tracks: downloadable.length },
          format,
          source
        ))
        if (!queued) return fail('playlist.download-error-message', true)
      }
      return true
    } catch {
      trackDownload('Download Failed', descriptor)
      return fail('playlist.download-error-message', true)
    } finally {
      pendingRef.current.delete(key)
      if (mountedRef.current) setPendingKeys([...pendingRef.current])
    }
  }, [dispatch, downloads, loadSongs])

  const retryPlaylistDownload = useCallback(() => {
    if (!downloadFailure?.retryable) return Promise.resolve(false)

    return requestPlaylistDownload(
      downloadFailure.playlist,
      downloadFailure.format,
      downloadFailure.source
    )
  }, [downloadFailure, requestPlaylistDownload])

  const dismissDownloadFailure = useCallback(() => {
    setDownloadFailure(null)
  }, [])

  return {
    dismissDownloadFailure,
    downloadFailure,
    getDownloadAvailability,
    isDownloadPreparing: pendingKeys.length > 0,
    requestPlaylistDownload,
    retryPlaylistDownload
  }
}

export default usePlaylistDownloads
